/**
 * A component that notifies users of and links to the Terms of Service and
 * Privacy Policy statements.
 *
 * The language used in this component specifically refers to signing up,
 * not logging in, but we can expand this component to do both based on a prop
 * if we can settle on which login statement to use.
 */
import {t} from "@lingui/core/macro";
import * as React from "react";

import type {Purpose} from "@khan/auth-shared";

import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

type DefaultProps = {
    color: string;
};

type Props = DefaultProps & {
    purpose: Purpose;
} & WithLocationProps; // Provided by withLocation HOC

class TermsOfServiceSignupStatement extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        color: "inherit",
    };

    // WARNING: This method will fail if the input path also contains a query
    // string!
    addAppropriateParams(path: string): string {
        const {location} = this.props;

        // If the current URL indicates the page is embedded
        // (has params embedded=1), we should propagate that forward to the
        // ToS and Privacy Policy links.
        const {embedded} = location.queryParams;
        const params = new URLSearchParams({embedded});
        return embedded ? path + "?" + params.toString() : path;
    }

    render(): React.ReactNode {
        const {purpose} = this.props;

        const action =
            purpose === "signup" ? t`signing up for` : t`logging in to`;
        const tosLink = this.addAppropriateParams(
            "/about/docs/khan-academy-terms-of-service",
        );
        const privacyPolicyLink = this.addAppropriateParams(
            "/about/privacy-policy",
        );
        const color = `color: ${this.props.color};`;

        const tosText = t`By ${action} Khan Academy, you agree to our <a href="${tosLink}" target="_blank" style="${color}">Terms of use</a> and <a href="${privacyPolicyLink}" target="_blank" style="${color}" >Privacy Policy</a>.`;

        return <span dangerouslySetInnerHTML={{__html: tosText}} />;
    }
}

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof TermsOfServiceSignupStatement,
        React.ComponentProps<typeof TermsOfServiceSignupStatement>
    >
>;

export default withLocation(
    TermsOfServiceSignupStatement,
) as React.ComponentType<ExportProps>;
